// Stage-visibility helpers shared by InteractiveGraph renderers and the
// overlay engine (applyOverlay ghost-dim guard).
//
// normalizeVisibilityValue / extractVisibilityCandidates /
// createStageVisibilityChecker were MOVED here verbatim from
// InteractiveGraph.js (module-level pure functions) so that the overlay
// predicate below shares the exact same normalization semantics the sliced
// renderers use — the two must never diverge. InteractiveGraph imports
// createStageVisibilityChecker back from this module.

const normalizeVisibilityValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return value.toString().trim().toLowerCase();
};

const extractVisibilityCandidates = (entry) => {
  if (entry === null || entry === undefined) return [];
  if (typeof entry === 'string' || typeof entry === 'number') {
    return [entry];
  }
  if (Array.isArray(entry)) {
    return entry;
  }
  if (typeof entry === 'object') {
    return [entry.id, entry.label, entry.name, entry.value, entry.key];
  }
  return [];
};

export const createStageVisibilityChecker = (visibleElements = []) => {
  const normalizedKeys = (visibleElements || [])
    .flatMap((entry) => extractVisibilityCandidates(entry))
    .filter((value) => value !== null && value !== undefined)
    .map((value) => normalizeVisibilityValue(value))
    .filter((value) => value.length > 0);

  if (!normalizedKeys.length) {
    return () => true;
  }

  const valueSet = new Set(normalizedKeys);
  return (...candidates) => {
    return candidates.some((candidate) => {
      const normalized = normalizeVisibilityValue(candidate);
      return normalized.length > 0 && valueSet.has(normalized);
    });
  };
};

/**
 * Axis-id discriminator — SINGLE SOURCE for the `id.includes('axis')` test.
 * Shared by predicate rule 5 below AND by applyOverlay's dim_irrelevant
 * keep-walk (InteractiveGraph), which must EXCLUDE axis nodes: rule 5 is
 * constant-true for them, so counting axes as "effectively visible keep
 * elements" would make the whole-action skip unreachable whenever a dim list
 * omits the axes. null/undefined → false (never axis).
 */
export const isAxisElementId = (eid) => eid != null && String(eid).includes('axis');

/**
 * effectiveVisible(eid) predicate factory for overlay actions.
 *
 * Decides whether an overlay action (highlight / dim / focus / value-tags /
 * micro-step / annotate) may target the element id on the CURRENT stage.
 * An id passes when ANY of:
 *  1. the stage has no visible_elements restriction (absent or empty list) —
 *     mirrors createStageVisibilityChecker's all-visible fallback;
 *  2. stageData.show_all_with_original_style === true;
 *  3. the element is stage-visible under createStageVisibilityChecker
 *     normalization with (id, label, type) candidate fan-out — renderers such
 *     as pie/venn/tree/dot list elements in visible_elements by LABEL, so a
 *     bare id-set membership test would misclassify them as ghosts;
 *  4. the id is named by the keyword-reveal prop (highlightedElements):
 *     getFocusVisualState renders those at full opacity even when
 *     stage-invisible, so overlays must keep targeting them;
 *  5. the id contains 'axis' — axis visibility is governed separately by
 *     getStageVisibilityFlags (same id.includes('axis') discriminator), so
 *     axis nodes keep their pre-existing overlay behavior unchanged.
 *
 * All inputs are read-only; neither the factory nor the returned predicate
 * mutates them (action objects / element_ids arrays must stay intact for
 * consume-once WeakSet identity and the voice dedup key).
 */
export const createEffectiveVisibilityPredicate = ({
  visibleElements,
  showAllWithOriginalStyle,
  highlightedElements,
  elements,
} = {}) => {
  if (showAllWithOriginalStyle === true) {
    return () => true;
  }
  const stageVisible = createStageVisibilityChecker(
    Array.isArray(visibleElements) ? visibleElements : []
  );
  const keywordSet = new Set(
    Array.isArray(highlightedElements) ? highlightedElements : []
  );
  const elementById = new Map();
  (Array.isArray(elements) ? elements : []).forEach((elem) => {
    if (elem && elem.id !== null && elem.id !== undefined) {
      elementById.set(elem.id, elem);
    }
  });
  return (eid) => {
    if (eid === null || eid === undefined) return false;
    const idStr = String(eid);
    if (isAxisElementId(eid)) return true;
    if (keywordSet.has(eid) || keywordSet.has(idStr)) return true;
    const elem = elementById.get(eid) ?? elementById.get(idStr);
    return stageVisible(eid, elem?.label, elem?.type);
  };
};
